import type { JSX } from 'react';

/**
 * Mapa esquematico de la Republica Mexicana, dibujado en SVG aqui mismo.
 *
 * Sin librerias de mapas y sin mosaicos de red, igual que en el prototipo. La
 * razon no es estetica: un mapa de mosaicos pide decenas de imagenes a un
 * tercero por cada carga, y esta plataforma se usa en sitio, con la red del
 * cliente y a veces desde un telefono con datos contados. Un contorno de 48
 * vertices pesa nada y se dibuja igual sin salir a internet.
 *
 * La silueta es APROXIMADA y sirve para ubicar, no para medir. Esta dicho
 * tambien en la pantalla, donde lo lee quien mira el mapa.
 */

/** La ventana geografica y el lienzo. De aqui sale la proyeccion. */
const MX = { lng0: -118.5, lng1: -86, lat0: 14, lat1: 33, w: 650, h: 414 } as const;

export const mxX = (lng: number): number => ((lng - MX.lng0) / (MX.lng1 - MX.lng0)) * MX.w;
export const mxY = (lat: number): number => ((MX.lat1 - lat) / (MX.lat1 - MX.lat0)) * MX.h;

/* Pares lng,lat. Contorno continental y peninsula de Baja California. */
const MX_TIERRA = [
  -117.1, 32.5, -114.7, 32.7, -111, 31.3, -108.2, 31.3, -106.5, 31.8, -104.5, 29.7, -102.3, 29.9,
  -101.4, 29.8, -100, 28, -99.1, 26.4, -97.1, 25.9, -97.8, 22.2, -96.4, 19.9, -94.5, 18.2, -92.9,
  18.6, -91.4, 18.7, -90.3, 19.9, -90.4, 21, -88, 21.6, -86.8, 21.5, -87, 20.2, -87.4, 19.6, -88.3,
  18.5, -89.2, 17.8, -90.9, 17.8, -91, 16.1, -92.2, 15.2, -92.2, 14.5, -94, 16.2, -96, 15.7, -98,
  16.3, -100, 16.8, -101.8, 17.9, -103.5, 18.7, -104.9, 19.3, -105.6, 20.5, -105.2, 21.5, -105.7,
  22.8, -106.9, 24, -108.4, 25.5, -109.4, 26.7, -109, 27.9, -110.6, 28.5, -112.1, 29.3, -113.1,
  31.2, -114.8, 31.8,
];

const MX_BAJA = [
  -114.8, 32.5, -117.1, 32.5, -116, 30.5, -115, 28.3, -114, 27.8, -112.8, 26, -112.1, 24.5, -111,
  23, -109.8, 22.9, -110, 24.2, -110.9, 25.3, -112, 26.9, -113.1, 28.2, -113.6, 29.6, -114.6, 30.9,
];

function mxPath(a: readonly number[]): string {
  let d = '';
  for (let i = 0; i < a.length; i += 2) {
    d += `${i ? 'L' : 'M'}${mxX(a[i] ?? 0).toFixed(1)} ${mxY(a[i + 1] ?? 0).toFixed(1)} `;
  }
  return `${d}Z`;
}

export interface PuntoMapa {
  lat: number;
  lng: number;
  /** Desplazamiento en pixeles del lienzo, para separar marcadores vecinos. */
  dx?: number;
  dy?: number;
  clase: 'm-tot' | 'm-ok' | 'm-bad' | 'm-menor';
  valor: number;
  /** Lo que se lee al posar el puntero y lo que anuncia el lector de pantalla. */
  nombre: string;
  r?: number;
  /** Punto chico y sin cifra dentro: la ciudad esta, pero no se etiqueta. */
  menor?: boolean;
}

export interface EtiquetaMapa {
  lat: number;
  lng: number;
  dx?: number;
  dy?: number;
  nombre: string;
}

/** Linea punteada de un grupo desplazado hasta su coordenada verdadera. */
export interface GuiaMapa {
  lat: number;
  lng: number;
  dy: number;
}

export function MapaMx({
  puntos,
  etiquetas = [],
  guias = [],
  alto = 460,
}: {
  puntos: PuntoMapa[];
  etiquetas?: EtiquetaMapa[];
  guias?: GuiaMapa[];
  alto?: number;
}): JSX.Element {
  /* El lienzo se estira hasta donde llega lo DIBUJADO, no hasta donde llega la
     silueta.

     Los grupos que se desplazan para no encimarse salen por abajo del
     contorno: Puebla desplazada 138 pixeles cae en y=443 sobre un lienzo de
     414, y el `overflow:hidden` del marco la cortaba a la mitad. Un marcador
     cortado es peor que uno encimado, porque no se ve que falta.

     Se calcula en vez de dejar un margen fijo: un margen que alcance para el
     peor desplazamiento encogeria el mapa siempre, incluso los dias en que
     ninguna ciudad se desplaza. */
  const caja = puntos.reduce<{ x0: number; y0: number; x1: number; y1: number }>(
    (c, p) => {
      const x = mxX(p.lng) + (p.dx ?? 0);
      const y = mxY(p.lat) + (p.dy ?? 0);
      const r = (p.r ?? 15) + 2;
      return {
        x0: Math.min(c.x0, x - r),
        y0: Math.min(c.y0, y - r),
        x1: Math.max(c.x1, x + r),
        y1: Math.max(c.y1, y + r),
      };
    },
    /* Arranca con la silueta: el mapa se ve completo aunque todos los
       marcadores caigan en una esquina. */
    { x0: 0, y0: 0, x1: MX.w, y1: MX.h },
  );
  /* Las etiquetas van 24 px arriba de su grupo; ese renglon tambien cuenta. */
  for (const e of etiquetas) {
    caja.y0 = Math.min(caja.y0, mxY(e.lat) + (e.dy ?? 0) - 12);
  }

  return (
    <div className="mapa" style={{ height: `${alto}px` }} data-prueba="mapa-mx">
      <svg
        viewBox={`${caja.x0.toFixed(1)} ${caja.y0.toFixed(1)} ${(caja.x1 - caja.x0).toFixed(1)} ${(caja.y1 - caja.y0).toFixed(1)}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`Mapa esquematico de la Republica Mexicana con ${puntos.length} marcador(es)`}
      >
        <path className="mx-tierra" d={mxPath(MX_TIERRA)} />
        <path className="mx-tierra" d={mxPath(MX_BAJA)} />

        {guias.map((g) => {
          const x = mxX(g.lng);
          const y = mxY(g.lat);
          return (
            <g className="mx-guia" key={`g-${x}-${y}`}>
              <line
                x1={x.toFixed(1)}
                y1={y.toFixed(1)}
                x2={x.toFixed(1)}
                y2={(y + g.dy).toFixed(1)}
              />
              <circle cx={x.toFixed(1)} cy={y.toFixed(1)} r="2.5" />
            </g>
          );
        })}

        {puntos.map((p, i) => {
          const x = mxX(p.lng) + (p.dx ?? 0);
          const y = mxY(p.lat) + (p.dy ?? 0);
          const texto = String(p.valor);
          /* El numero se encoge cuando no cabe en el circulo. Cuatro cifras a
             10px se salen del marcador y se leen peor que a 8px dentro. */
          const tam = texto.length > 3 ? 8 : texto.length > 2 ? 9 : 10;
          return (
            <g className={`mx-pin ${p.clase}`} key={`${p.nombre}-${i}`}>
              <title>{`${p.nombre} · ${texto}`}</title>
              <circle cx={x.toFixed(1)} cy={y.toFixed(1)} r={p.r ?? 15} />
              {!p.menor && (
                <text x={x.toFixed(1)} y={(y + 3.5).toFixed(1)} textAnchor="middle" fontSize={tam}>
                  {texto}
                </text>
              )}
            </g>
          );
        })}

        {etiquetas.map((e) => (
          <text
            key={e.nombre}
            x={(mxX(e.lng) + (e.dx ?? 0)).toFixed(1)}
            y={(mxY(e.lat) + (e.dy ?? 0)).toFixed(1)}
            textAnchor="middle"
            className="mx-ciu"
          >
            {e.nombre}
          </text>
        ))}
      </svg>
      {puntos.length === 0 && <div className="mapa-vacio">Sin coordenadas para dibujar</div>}
    </div>
  );
}

/**
 * Coloca los grupos de tres marcadores evitando que se encimen.
 *
 * En el Bajio hay cuatro ciudades a menos de dos grados: sus marcadores caen
 * en el mismo renglon y se tapan. Cuando un grupo chocaria con otro ya
 * colocado se desplaza en vertical y se deja una linea guia hasta su
 * coordenada real, para que el mapa no mienta el lugar.
 */
export function acomodar(): (x: number, y: number) => number {
  const ANCHO = 104;
  const ALTO = 40;
  const puestos: { x: number; y: number }[] = [];
  return (x, y) => {
    for (const d of [0, 46, -46, 92, -92, 138, -138]) {
      if (!puestos.some((q) => Math.abs(q.x - x) < ANCHO && Math.abs(q.y - (y + d)) < ALTO)) {
        puestos.push({ x, y: y + d });
        return d;
      }
    }
    /* Siete intentos y sigue chocando: se deja en su lugar verdadero. Mejor
       dos grupos encimados que uno a doscientos pixeles de su ciudad. */
    puestos.push({ x, y });
    return 0;
  };
}
